import type { MemberOutcome } from '@/features/OrgMembers/lib/memberOutcomes'
import type {
  AddOrganizationMemberOutcome,
  AddOrganizationMemberResult,
  CreateAccountConflictReason,
  CreateAccountOwnerResult,
} from '@/generated/http-clients/v3ApiSchemas'

const KEY_PREFIX = 'accountManagement.createAccount'

export type ConflictMessageKeys = {
  titleKey: string
  descriptionKey?: string
}

const CONFLICT_MESSAGE_KEYS: Record<
  CreateAccountConflictReason,
  ConflictMessageKeys
> = {
  OWNER_EXISTS: {
    titleKey: `${KEY_PREFIX}.errors.conflict.OWNER_EXISTS.title`,
    descriptionKey: `${KEY_PREFIX}.errors.conflict.OWNER_EXISTS.description`,
  },
  OWNER_SSO_DOMAIN_NO_IDENTITY: {
    titleKey: `${KEY_PREFIX}.errors.conflict.OWNER_SSO_DOMAIN_NO_IDENTITY.title`,
    descriptionKey: `${KEY_PREFIX}.errors.conflict.OWNER_SSO_DOMAIN_NO_IDENTITY.description`,
  },
  OWNER_NOT_SELECTABLE: {
    titleKey: `${KEY_PREFIX}.errors.conflict.OWNER_NOT_SELECTABLE.title`,
    descriptionKey: `${KEY_PREFIX}.errors.conflict.OWNER_NOT_SELECTABLE.description`,
  },
}

const UNKNOWN_CONFLICT: ConflictMessageKeys = {
  titleKey: `${KEY_PREFIX}.errors.conflict.unknown.title`,
}

const ADDED_OR_PRESENT_OUTCOMES: ReadonlySet<AddOrganizationMemberOutcome> =
  new Set(['CREATED', 'ATTACHED_EXISTING', 'ALREADY_MEMBER'])

function isConflictReason(
  reason: string | undefined,
): reason is CreateAccountConflictReason {
  return reason !== undefined && Object.hasOwn(CONFLICT_MESSAGE_KEYS, reason)
}

export function ownerOutcomeKey(owner: CreateAccountOwnerResult): string {
  return `${KEY_PREFIX}.success.owner.${owner.outcome}`
}

export function conflictMessageKeys(
  reason: string | undefined,
): ConflictMessageKeys {
  if (isConflictReason(reason)) {
    return CONFLICT_MESSAGE_KEYS[reason]
  }
  return UNKNOWN_CONFLICT
}

function isResultNotAdded(result: AddOrganizationMemberResult): boolean {
  if (ADDED_OR_PRESENT_OUTCOMES.has(result.outcome)) {
    return false
  }
  return !(
    result.outcome === 'FAILED' && result.reason === 'ROLE_ASSIGNMENT_FAILED'
  )
}

export function hasMemberNotAdded(outcomes: readonly MemberOutcome[]): boolean {
  return outcomes.some(
    (outcome) => outcome.kind !== 'sent' || isResultNotAdded(outcome.result),
  )
}
